import React from 'react';
import { ArrowLeft, Settings as SettingsIcon, Info } from 'lucide-react';
import UserMenu from '../components/UserMenu';

/**
 * "Settings" — account-level settings screen.
 *
 * VetFlow is staff-only, and staff accounts have no editable surface here:
 * identity and contact details are mastered in Microsoft 365 / Entra ID and
 * re-synced from Azure AD on every sign-in, and there is no PATCH endpoint
 * for staff. This renders an explanatory read-only panel rather than
 * disabled controls that would imply editability.
 *
 * The former client notification-preference form was retired with the
 * insurance claims domain (the PATCH /api/profile/me endpoint and the
 * notification service are gone).
 */

const AccountSettings = ({ navigateTo }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigateTo('home')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center">
                <SettingsIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Settings</h1>
                <p className="text-sm text-gray-500">Account preferences</p>
              </div>
            </div>
            <UserMenu navigateTo={navigateTo} />
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Staff have no editable settings here — identity and contact
            details are mastered in Microsoft 365, so render an explanatory
            panel rather than disabled controls that imply editability. */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
              <Info className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Managed in Microsoft 365</h2>
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                Your account and notification settings are managed in Microsoft 365 / Entra ID
                and synchronise from Azure AD each time you sign in. There is nothing to edit
                here — contact your administrator to change your details.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
